import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { INVOICE_DETAIL_ROUTE_PATH_LIST } from 'routes/constants';

// The kind of record or list the user is looking at. Also the key into the suggested-prompt
// catalogue (see pagePrompts.ts). Only domains the assistant has a lookup tool for belong here:
// announcing context it cannot read leads to answers about the wrong thing. Add a new value here
// + a rule below + a `ai-chat.floating.page-type.*` label + (optionally) prompts.
export type PageObjectType =
    | 'dashboard'
    | 'contact'
    | 'matter'
    | 'invoice'
    | 'effort'
    | 'timecard'
    | 'employee'
    | 'group'
    | 'groups'
    | 'team'
    | 'office'
    | 'practice-area'
    | 'job-role'
    | 'job-position'
    | 'experience-record'
    | 'contacts'
    | 'matters'
    | 'invoices'
    | 'timecards'
    | 'efforts'
    | 'employees'
    | 'teams'
    | 'offices'
    | 'practice-areas'
    | 'job-roles'
    | 'job-positions'
    | 'experience-records'
    | 'pricelists';

interface PageContextRule {
    objectType: PageObjectType;
    kind: 'detail' | 'list';
    // react-router path patterns (absolute) matched against the current pathname, exact.
    patterns: string[];
}

// Ordered most-specific first. Detail rules require a numeric `:id` (so `/contact/new` and other
// non-numeric segments fall through to the generic fallback). Extend this list to teach the
// assistant about more pages — everything else gets a generic experience (null context).
const RULES: PageContextRule[] = [
    { objectType: 'dashboard', kind: 'list', patterns: ['/home'] },
    // --- record detail pages ---
    { objectType: 'invoice', kind: 'detail', patterns: [...INVOICE_DETAIL_ROUTE_PATH_LIST] },
    {
        objectType: 'contact',
        kind: 'detail',
        patterns: ['/contact/:id', '/contact/:id/:tab', '/contact/:id/invoices/:invoiceTab'],
    },
    {
        objectType: 'matter',
        kind: 'detail',
        patterns: ['/matter/:id', '/matter/:id/:tab', '/matter/:id/invoices/:invoiceTab', '/matter/:id/edit'],
    },
    { objectType: 'effort', kind: 'detail', patterns: ['/effort/:id', '/effort/:id/:tab'] },
    { objectType: 'timecard', kind: 'detail', patterns: ['/timecards/:id'] },
    {
        objectType: 'employee',
        kind: 'detail',
        patterns: ['/employee/:id', '/employee/:id/:tab'],
    },
    { objectType: 'group', kind: 'detail', patterns: ['/group/:id', '/group/:id/:tab'] },
    { objectType: 'team', kind: 'detail', patterns: ['/team/:id', '/team/:id/:tab'] },
    { objectType: 'office', kind: 'detail', patterns: ['/office/:id', '/office/:id/:tab'] },
    {
        objectType: 'practice-area',
        kind: 'detail',
        patterns: ['/practice-area/:id', '/practice-area/:id/:tab'],
    },
    {
        objectType: 'job-role',
        kind: 'detail',
        patterns: ['/job-role/:id', '/job-role/:id/:tab', '/admin/job-roles/:id', '/admin/job-roles/:id/:tab'],
    },
    {
        objectType: 'job-position',
        kind: 'detail',
        patterns: [
            '/job-position/:id',
            '/job-position/:id/:tab',
            '/admin/job-positions/:id',
            '/admin/job-positions/:id/:tab',
        ],
    },
    { objectType: 'experience-record', kind: 'detail', patterns: ['/experience-records/:id'] },
    // --- list pages ---
    { objectType: 'contacts', kind: 'list', patterns: ['/contacts'] },
    { objectType: 'groups', kind: 'list', patterns: ['/groups'] },
    { objectType: 'matters', kind: 'list', patterns: ['/matters'] },
    {
        objectType: 'invoices',
        kind: 'list',
        patterns: ['/invoices', '/invoices/:invoiceTab', '/prepayments'],
    },
    {
        objectType: 'timecards',
        kind: 'list',
        patterns: ['/timecards', '/timecards/main', '/timecards/draft'],
    },
    { objectType: 'efforts', kind: 'list', patterns: ['/efforts'] },
    { objectType: 'employees', kind: 'list', patterns: ['/employees'] },
    { objectType: 'teams', kind: 'list', patterns: ['/teams'] },
    { objectType: 'offices', kind: 'list', patterns: ['/offices'] },
    { objectType: 'practice-areas', kind: 'list', patterns: ['/practice-areas'] },
    { objectType: 'job-roles', kind: 'list', patterns: ['/job-roles', '/admin/job-roles'] },
    { objectType: 'job-positions', kind: 'list', patterns: ['/job-positions', '/admin/job-positions'] },
    { objectType: 'experience-records', kind: 'list', patterns: ['/experience-records'] },
    { objectType: 'pricelists', kind: 'list', patterns: ['/pricelists'] },
];

export interface PageContext {
    objectType: PageObjectType;
    // Record id for detail pages, null for lists.
    id: string | null;
    url: string;
}

const isNumericId = (value: string | undefined): value is string => value != null && /^\d+$/.test(value);

// Identity only — object type + id + relative url. Sent verbatim to the backend `pageContext` field
// (never the page's loaded data); the assistant fetches details through the existing lookup tools.
export const resolvePageContext = (pathname: string): PageContext | null => {
    for (const rule of RULES) {
        for (const pattern of rule.patterns) {
            const match = matchPath(pattern, pathname);
            if (!match) {
                continue;
            }

            if (rule.kind === 'detail') {
                const id = match.params.id;
                if (!isNumericId(id)) {
                    continue;
                }
                return { objectType: rule.objectType, id, url: pathname };
            }

            return { objectType: rule.objectType, id: null, url: pathname };
        }
    }

    return null;
};

/** Resolves the current route to the record/list identity the floating assistant should know about. */
export const usePageContext = (): PageContext | null => {
    const { pathname } = useLocation();
    return useMemo(() => resolvePageContext(pathname), [pathname]);
};
